import React, { PureComponent } from 'react';
import { Paper, Table, TableHead, TableBody, TableRow, TableCell, 
        Toolbar, Typography, Tooltip} from '@material-ui/core';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';

import theWheel from '../assets/theWheel.svg';
import theWheel2 from '../assets/theWheel2.svg';
import ironUmp from '../assets/ironUmp.svg';
import { getEliteStatus } from './statistics/gameStatistics';

const styles = theme => ({
  root: {
    maxWidth: 1060,
    margin: 'auto',
  },
  toolbar: {
    paddingRight: theme.spacing.unit,
  },
  spacer: {
    flex: '1 1 100%',
  },
  title: {
    flex: '0 0 auto',
  },
  tablerow: {
    height: 36
  },
  icon: {
    marginLeft: 12,
    height: 24,
    verticalAlign: 'middle'
  }
});

class WheelReport extends PureComponent {

  componentDidMount() {
    const { userSeason, getFullSeasonStats, getSeasonDivisions, seasons } = this.props;
    
    //Get the data required for the report - Divisions and Umpires for the specified Season
    if (userSeason) {
      getSeasonDivisions(seasons[userSeason].parentSeasonId);
      getFullSeasonStats(seasons[userSeason].parentSeasonId);
    };
  }

  componentDidUpdate(prevProps){
    const { userSeason, getFullSeasonStats, getSeasonDivisions, seasons } = this.props;
    //Re-retrieve the Wheel Report data if the Season changes
    if (userSeason !== prevProps.userSeason) {
      getSeasonDivisions(seasons[userSeason].parentSeasonId);
      getFullSeasonStats(seasons[userSeason].parentSeasonId);
    }
  }

  render() {
    const { statsSeasons, statsSeasonsList, seasonDivisions, seasonDivisionsList,
            umpiresStats, umpiresStatsList, 
            fetchingSeasonDivisionsStatus, fetchingFullSeasonStatsStatus } = this.props;
    const { classes } = this.props;

    return (
      <Paper className={classes.root}>
        <Toolbar className={classes.toolbar}>
            <div  className={classes.title}>
              <Typography variant="h5">
                Umpire Statistics
              </Typography>
            </div>
            <div className={classes.spacer} />
        </Toolbar>
        
        {(fetchingSeasonDivisionsStatus === 'Loaded' && fetchingFullSeasonStatsStatus === 'Loaded') 
        && (
          <Table padding="none">
            <TableHead>
              <TableRow>
                <TableCell padding="dense" style={{width: 280}}>
                  <Typography variant="body1">Name</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={<Typography color="inherit" variant="body2">Umpire Level</Typography>}>
                    <Typography variant="body1">Level</Typography>
                  </Tooltip>
                </TableCell>
                {seasonDivisionsList.map((divId) => {
                  let division = seasonDivisions[divId].shortName;
                  //Create a very short division name, stripping out numbers if Division short name doesn't begin with a number
                  if (division.length > 1 && isNaN(division.charAt(0))) division = division.replace(/[^a-zA-Z]/g,"");
                  const divisionTip = seasonDivisions[divId].name + ' (plate / base)';
                  return (
                    <TableCell align="center" key={divId}>
                      <Tooltip title={<Typography color="inherit" variant="body2">{divisionTip}</Typography>}>
                        <Typography variant="body1">{division}</Typography>
                      </Tooltip>
                    </TableCell>);
                })}
                {statsSeasonsList.map((seasonId) => {
                  let season = statsSeasons[seasonId].shortName;
                  //Create a very short Season name, stripping out numbers if Season short name doesn't begin with a number
                  if (season.length > 1 && isNaN(season.charAt(0))) season = season.replace(/[^a-zA-Z]/g,"");
                  return (
                    <TableCell align="center" key={seasonId}>
                      <Tooltip title={<Typography color="inherit" variant="body2">{statsSeasons[seasonId].name}</Typography>}>
                        <Typography variant="body1">{season}</Typography>
                      </Tooltip>
                    </TableCell>);
                })}
                <TableCell align="center">
                  <Tooltip title={<Typography color="inherit" variant="body2">Total Games</Typography>}>
                    <Typography variant="body1">Total</Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {umpiresStatsList.map((umpId) => {
                const umpire = umpiresStats[umpId];
                const hasWheel = umpire.hasWheel || umpire.hasWheel2;
                const wheelSrc = hasWheel ? (umpire.hasWheel2 ? theWheel2 : theWheel) : null;
                const wheelTxt = hasWheel ? (umpire.hasWheel2 ? "The Wheel 2" : "The Wheel") : "";
                const isIron = umpire.isIronUmp;
                const gameTotal = umpire.plateTotal + umpire.baseTotal;
                const eliteStatus = getEliteStatus(gameTotal);
                const isYouth = umpire.isYouth;
                return (
                  <TableRow className={classes.tablerow} key={umpId} hover>
                    <TableCell padding="dense" style={{width: 280}}>
                      <Typography variant="body1">
                        {umpire.firstName} {umpire.lastName}{isYouth ? ' Y' : ''}
                        {eliteStatus.icon && 
                          <Tooltip title={<Typography color="inherit" variant="body2">{eliteStatus.title}</Typography>}><img src={eliteStatus.icon} alt={eliteStatus.title} className={classes.icon}/></Tooltip>}
                        {hasWheel && 
                          <Tooltip title={<Typography color="inherit" variant="body2">{wheelTxt}</Typography>}><img src={wheelSrc} alt="The Wheel" className={classes.icon}/></Tooltip>}
                        {isIron && 
                          <Tooltip title={<Typography color="inherit" variant="body2">IronUmp</Typography>}><img src={ironUmp} alt="Iron Ump" className={classes.icon}/></Tooltip>}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1">{umpire.umpireLevel}</Typography>
                    </TableCell>
                    {seasonDivisionsList.map((divId) => {
                      const division = umpire.divisionCounts[divId];
                      const plate = division.plate > 0 ? division.plate : "-";
                      const base = division.base > 0 ? division.base : "-";
                      const label = division.plate + division.base > 0 ? `${plate} / ${base}` : "-"
                      return <TableCell align="center" key={divId}><Typography variant="body1">{label}</Typography></TableCell>;
                    })}
                    {statsSeasonsList.map((seasonId) => {
                      let season = umpire.seasonCounts[seasonId];
                      let total = season.plate + season.base;
                      return (
                        <TableCell align="center" key={seasonId}>
                          <Typography variant="body1">{total}</Typography>
                        </TableCell>);
                    })}
                    <TableCell align="center"><Typography variant="body1">{gameTotal}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}        
      </Paper>
    );
  }
}

WheelReport.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

export default withStyles(styles, { withTheme: true })(WheelReport);



// WEBPACK FOOTER //
// ./src/components/WheelReport.js